import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { useWebSocketContext } from "./WebSocketProvider";
import { ReadyState } from "react-use-websocket";
import dayjs from "dayjs";

const DataContext = createContext(null);

const API_BASE = process.env.REACT_APP_API_URL || "http://localhost:3013/api";
const API_BASE_URL = `${API_BASE}/sab`;
const api = axios.create({
  baseURL: API_BASE_URL,
});

export const DataProvider = ({ children }) => {
  const { lastMessage, sendMessage, readyState } = useWebSocketContext();

  const [dashboard, setDashboard] = useState([]);
  const [events, setEvents] = useState([]);
  const [mainboard, setMainboard] = useState([]);
  const [scrollboard, setScrollboard] = useState([]);
  const prevReadyState = useRef(readyState);

  // GET /dashboard is public — do not gate on login token
  const fetchDashboard = useCallback(async () => {
    try {
      const response = await api.get(`/dashboard`);
      const transformData = (data) => 
        data.map(item => ({ ...item, id: item._id || item.id }));
      setDashboard(transformData(response.data));
      console.log("✅ Dashboard loaded");
    } catch (error) {
      console.error("❌ Failed to fetch dashboard:", error);
    }
  }, []);

  // Fetch public data (no auth required, uses polling)
  const fetchPublicData = async () => {
    try {
      console.log("🔄 Fetching public data from:", API_BASE_URL);
      
      const [eventsRes, mainboardRes, scrollboardRes] =
        await Promise.all([
          api.get(`/events`),
          api.get(`/mainboard`),
          api.get(`/scrollboard`),
        ]);

      const transformData = (data) => 
        data.map(item => ({ ...item, id: item._id || item.id }));

      console.log("📋 Mainboard response:", mainboardRes.data?.length, "items");
      
      setEvents(transformData(eventsRes.data));
      setMainboard(transformData(mainboardRes.data));
      setScrollboard(transformData(scrollboardRes.data));

      console.log("✅ Public data refreshed @ " + dayjs().format("HH:mm:ss"));
    } catch (error) {
      console.error("❌ Failed to fetch public data:", error);
      console.error("❌ Error details:", error.response?.data || error.message);
    }
  };

  // Poll dashboard so Live / Kirtan / Welcome still switch if the socket drops
  useEffect(() => {
    fetchDashboard();
    const pollInterval = setInterval(fetchDashboard, 15 * 1000);
    return () => clearInterval(pollInterval);
  }, [fetchDashboard]);

  // Catch up immediately after a reconnect (missed WS broadcasts)
  useEffect(() => {
    if (readyState === ReadyState.OPEN && prevReadyState.current !== ReadyState.OPEN) {
      fetchDashboard();
    }
    prevReadyState.current = readyState;
  }, [readyState, fetchDashboard]);

  // ✅ Poll public data every 30 seconds (no auth required)
  useEffect(() => {
    fetchPublicData();
    const pollInterval = setInterval(fetchPublicData, 30 * 1000);
    return () => clearInterval(pollInterval);
  }, []);

  // ✅ Handle WebSocket Messages (ONLY for updates, not initial load)
  useEffect(() => {
    if (lastMessage) {
      try {
        const data = JSON.parse(lastMessage.data);
        console.log("📩 Received WebSocket Update:", data);

        const { type, action, payload = data } = data;
       
        switch (type) {
          case "events":
            updateState(setEvents, action, payload.data);
            break;
          case "dashboard":
            updateState(setDashboard, action, payload.data);
            break;
          case "mainboard":
            updateState(setMainboard, action, payload.data);
            break;
          case "scrollboard":
            updateState(setScrollboard, action, payload.data);
            break;
          case "pong":
            console.log("pong received @ : " + dayjs().format("DD MMM YYYY hh:mm:ss"));
            break;
          case "auth":
            console.log(payload);
            break;
          default:
            console.warn("⚠️ Unknown WebSocket message type:", type);
        }
      } catch (error) {
        console.error("❌ WebSocket message parse error:", error);
      }
    }
  }, [lastMessage]);

  // Generic function to update state based on action type
  const updateState = (setState, action, payload) => {
    if (!payload) return;
    
    // Transform payload to ensure it has an 'id' field
    const transformedPayload = typeof payload === 'object' && !Array.isArray(payload)
      ? { ...payload, id: payload._id || payload.id }
      : payload;

    setState((prevData) => {
      switch (action) {
        case "create":
          return [...prevData, transformedPayload]; // Append new item
        case "update": {
          const payloadId = transformedPayload.id || transformedPayload._id;
          const exists = prevData.some((item) => (item.id || item._id) == payloadId);
          if (!exists) return [...prevData, transformedPayload];
          return prevData.map((item) =>
            (item.id || item._id) == payloadId
              ? transformedPayload
              : item
          );
        }
        case "delete":
          const deleteId = typeof payload === 'string' ? payload : (payload.id || payload._id);
          return prevData.filter((item) => (item.id || item._id) !== deleteId); // Remove item
        default:
          return prevData; // Keep previous state if action is unknown
      }
    });
  };

  // ✅ CRUD Operations with API Calls
  const addItem = async (type, item) => {
    try {
      const response = await api.post(`/${type}`, item);

      const newItem = { ...item, id: response.data.id || response.data._id, _id: response.data.id || response.data._id };

      switch (type) {
        case "dashboard":
          setDashboard([...dashboard, newItem]);
          break;
        case "events":
          setEvents([...events, newItem]);
          break;
        case "mainboard":
          setMainboard([...mainboard, newItem]);
          break;
        case "scrollboard":
          setScrollboard([...scrollboard, newItem]);
          break;
        default:
          return;
      }

      sendMessage(JSON.stringify({ type, action: "create", data: newItem }));
    } catch (error) {
      console.error(`❌ Failed to add ${type}:`, error);
    }
  };

  const updateItem = async (type, id, updatedFields) => {
    try {
      const response = await api.put(`/${type}/${id}`, updatedFields);
      const updatedItem = { ...updatedFields, id, _id: id };
    
      switch (type) {
        case "dashboard":
          setDashboard(
            dashboard.map((item) => ((item.id || item._id) == id ? updatedItem : item))
          );
          break;
        case "events":
          setEvents(
            events.map((item) => ((item.id || item._id) == id ? updatedItem : item))
          );
          break;
        case "mainboard":
          setMainboard(
            mainboard.map((item) => ((item.id || item._id) == id ? updatedItem : item))
          );
          break;
        case "scrollboard":
          setScrollboard(
            scrollboard.map((item) => ((item.id || item._id) == id ? updatedItem : item))
          );
          break;
        default:
          return;
      }
      
      sendMessage(
        JSON.stringify({ type, action: "update", data: updatedItem })
      );
    } catch (error) {
      console.error(`❌ Failed to update ${type}:`, error);
    }
  };

  const deleteItem = async (type, id) => {
    try {
      await api.delete(`/${type}/${id}`);

      switch (type) {
        case "events":
          setEvents(events.filter((item) => (item.id || item._id) !== id));
          break;
        case "mainboard":
          setMainboard(mainboard.filter((item) => (item.id || item._id) !== id));
          break;
        case "scrollboard":
          setScrollboard(scrollboard.filter((item) => (item.id || item._id) !== id));
          break;
        default:
          return;
      }

      sendMessage(JSON.stringify({ type, action: "delete", data: id }));
    } catch (error) {
      console.error(`❌ Failed to delete ${type}:`, error);
    }
  };

  return (
    <DataContext.Provider
      value={{
        dashboard,
        events,
        mainboard,
        scrollboard,
        addItem,
        updateItem,
        deleteItem,
      }}
    >
      {children}
    </DataContext.Provider>
  );
};

export const useDataContext = () => useContext(DataContext);
